/**
 * «Guardar en la libreta» — save the counterparty a party block holds to the address book (#159).
 *
 * Beside «Soy yo» on each party block, and on its own on the route sheet's «Contratante», which has
 * no «Soy yo». It saves what the block holds: the name and the NIF, and the address where the block
 * has one. So it is offered only once both of the first two are there.
 *
 * **Whose book.** Always the caller's own: the company's, or a Gestor Individual's. The server
 * decides that from the credential, and nothing here chooses or says otherwise (spec §2).
 *
 * **When the book already has the NIF**, the server refuses with `409 ADDRESS_BOOK_ENTRY_EXISTS` and
 * names that entry. Saving again would be a second entry for one counterparty, which the server
 * never allows; what the user means is to bring that entry up to date, so that is what it offers,
 * and it writes only once they agree. The update writes the block's name and NIF, and its address
 * where it has one; a block with none to give — the route sheet's, or a blank «Domicilio» — keeps the
 * address the entry holds, read from the book first, since the update replaces all three and the
 * user never saw that address. If the book no longer shows that entry, nothing is sent and the dialog
 * says so. Any other refusal — the company's own NIF above all, which «Soy yo» fills — is shown as
 * `apiErrorMessage` words it, as the portal's other single-click writes are: the catalogue's sentence
 * for its code, the portal's own for a status it knows, otherwise the server's words.
 */

import { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { unwrap } from '@deca/api'

import { entryInputOf, type AddressBookEntryInput } from '../app/addressBook.ts'
import { api } from '../app/api.ts'
import { apiErrorMessage, existingAddressBookEntryOf } from '../app/errors.ts'
import { invalidateAddressBook } from '../app/queries.ts'
import { Dialog } from './Dialog.tsx'
import type { PartyColumns } from './partyFill.tsx'
import { Button, Notice } from './primitives.tsx'
import { useToast } from './Toast.tsx'

const filled = (value: unknown) => typeof value === 'string' && value.trim() !== ''

export function SaveToBookAction({
  columns,
  accessibleName,
}: {
  columns: PartyColumns
  /**
   * The button's name for assistive technology where the visible text alone would be ambiguous:
   * grouping's «Datos comunes» holds one per party, as it holds one «Soy yo» per party (#137). A
   * section card's header needs none, since the card already says whose block it is.
   */
  accessibleName?: string
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const client = useQueryClient()
  const form = useFormContext()
  // Watched, not read on click: the button has to follow the block as it is typed into.
  const [name, nif] = useWatch({ control: form.control, name: [columns.name, columns.nif] }) as unknown[]

  /** The entry the server named, and what was being saved over it, while the user decides. */
  const [existing, setExisting] = useState<{ entryId: string; body: AddressBookEntryInput } | null>(null)
  const [busy, setBusy] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)

  const ready = filled(name) && filled(nif)

  /**
   * The address an update writes: the block's own where it has one; otherwise the one the entry
   * already holds, read from the book, so agreeing to the update never erases an address the user
   * did not see. `undefined` when the book no longer shows the entry under this NIF: the update is
   * then not sent, since writing without the entry's address could erase it.
   */
  const addressFor = async ({ entryId, body }: { entryId: string; body: AddressBookEntryInput }) => {
    if (body.address) return body.address
    const book = await unwrap(api.GET('/api/v1/address-book', { params: { query: { q: body.nif ?? '' } } }))
    const entry = book.data.find((row) => row.entryId === entryId)
    return entry ? entry.address : undefined
  }

  const save = async () => {
    const body = entryInputOf('PARTY', {
      name: String(name),
      nif: String(nif),
      address: columns.address ? (form.getValues(columns.address) as string | null | undefined) : null,
    })
    setBusy(true)
    try {
      await unwrap(api.POST('/api/v1/address-book/entries', { body }))
      invalidateAddressBook(client)
      toast.success(t('create.saveToBook.saved', { name: body.name }))
    } catch (caught) {
      const entryId = existingAddressBookEntryOf(caught)
      if (entryId) {
        setUpdateError(null)
        setExisting({ entryId, body })
      } else {
        toast.error(apiErrorMessage(caught, t))
      }
    } finally {
      setBusy(false)
    }
  }

  const update = async () => {
    if (!existing) return
    setBusy(true)
    setUpdateError(null)
    try {
      const address = await addressFor(existing)
      if (address === undefined) {
        setUpdateError(t('errors.code.ADDRESS_BOOK_ENTRY_NOT_FOUND'))
        return
      }
      await unwrap(
        api.PUT('/api/v1/address-book/entries/{entryId}', {
          params: { path: { entryId: existing.entryId } },
          body: { ...existing.body, address },
        }),
      )
      invalidateAddressBook(client)
      setExisting(null)
      toast.success(t('create.saveToBook.updated', { name: existing.body.name }))
    } catch (caught) {
      // In the dialog, where the user is looking: the entry may have been removed meanwhile.
      setUpdateError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        icon="book"
        onClick={() => void save()}
        disabled={!ready || busy}
        loading={busy && existing === null}
        aria-label={accessibleName}
      >
        {t('create.saveToBook.action')}
      </Button>

      <Dialog
        open={existing !== null}
        title={t('create.saveToBook.existsTitle')}
        subtitle={t('create.saveToBook.existsBody', { nif: existing?.body.nif ?? '' })}
        onClose={() => setExisting(null)}
        busy={busy}
        footer={
          <>
            <Button onClick={() => setExisting(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => void update()} disabled={busy} loading={busy}>
              {t('create.saveToBook.update')}
            </Button>
          </>
        }
      >
        <div className="stack-sm">
          <p>{t('create.saveToBook.existsQuestion')}</p>
          {updateError ? <Notice tone="danger">{updateError}</Notice> : null}
        </div>
      </Dialog>
    </>
  )
}
